import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { FULL_VERSION } from '../version';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.success) {
      toast.success(`Bienvenido, ${res.user.name}`);
      const role = res.user.role;
      navigate(role === 'admin' ? '/admin' : '/app');
    } else {
      toast.error(res.error || 'Error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero · pc-blue gradient — replicates the cover slide */}
      <div className="pc-hero relative overflow-hidden lg:flex-[1.2] flex flex-col justify-between px-6 sm:px-12 lg:px-16 py-10 lg:py-16">
        {/* Top bar: logo + product line */}
        <div className="fade-in">
          <div className="inline-flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg pc-logo-tile flex items-center justify-center">
              <span className="text-white font-semibold text-[18px] leading-none tracking-tight">P</span>
            </div>
            <span className="text-[18px] tracking-tight font-medium text-white">PriceCapture</span>
          </div>
          <p className="mt-12 lg:mt-16 text-[11px] tracking-[0.22em] uppercase text-white/65">
            Sistema de captura de precios
          </p>
        </div>

        {/* Big headline */}
        <div className="my-10 lg:my-0">
          <h1 className="text-white font-semibold tracking-tight leading-[0.92] text-[clamp(56px,9vw,128px)]">
            PriceCapture
          </h1>
        </div>

        {/* Footer meta */}
        <div className="flex items-end justify-end gap-4 text-white/80">
          <div className="text-right">
            <p className="text-[10px] tracking-[0.22em] uppercase text-white/55">Versión</p>
            <p className="mt-1 text-[13px] font-medium tracking-tight" data-testid="login-app-version">{FULL_VERSION}</p>
          </div>
        </div>
      </div>

      {/* Login card */}
      <div className="bg-[#f8f6f1] flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-in">
          <h2 className="font-serif text-[40px] leading-[1.05] tracking-tight mb-2">
            Bienvenido<br/><em className="text-[#3b6cff]">de vuelta.</em>
          </h2>
          <p className="text-[13.5px] text-[#5a5a5a] mb-10">
            Inicia sesión para continuar capturando precios.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[11.5px] tracking-wider uppercase text-[#8a8579]">Correo</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="tu@correo.gt"
                className="h-11 bg-transparent border-[#d4cfc4] focus:border-[#0f1d6f] rounded-md"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[11.5px] tracking-wider uppercase text-[#8a8579]">Contraseña</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-transparent border-[#d4cfc4] focus:border-[#0f1d6f] rounded-md"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full h-11 bg-[#0f1d6f] hover:bg-[#0b1a52] text-white rounded-full mt-2 group"
              data-testid="login-submit"
            >
              {loading ? 'Cargando…' : (<>Iniciar sesión <ArrowRight size={15} className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-[13px] text-[#5a5a5a]">
            ¿Nuevo capturador? <Link to="/register" className="text-[#0f1d6f] underline underline-offset-4 hover:text-[#3b6cff] transition">Crear cuenta</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
